import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Role } from 'src/app/backend-client/models/Backend/Core/Infrastructure/Aggregates/Identity/role';
import { MxIdentityService, MxMenuService } from '../../services';
import { SzShellService } from '../../services/sz-shell.service';
import { szMenuIcon } from './sz-icons';

export interface SzMenuEntry {
  key: string;
  label: string;
  icon: string;
  routerLink?: any[];
  children?: SzMenuEntry[];
  open?: boolean;
}

/**
 * Oldalmenü a prototípus szerint: 264 px, csoportok nyitható allistával, 72 px ikonsáv összecsukva
 * (1280 px alatt automatikusan), 1024 px alatt fiók. A menü tartalma a MxMenuService jogosultság-szűrt modellje.
 */
@Component({
  standalone: false,
  selector: 'sz-sidebar',
  templateUrl: './sz-sidebar.component.html',
  styleUrls: ['./sz-sidebar.component.scss'],
})
export class SzSidebarComponent implements OnInit, OnDestroy {
  entries: SzMenuEntry[] = [];
  collapsed = false;
  drawerOpen = false;
  narrow = false;
  userName = '';
  userSub = '';
  initials = '';
  private subs = new Subscription();
  private forcedCollapsed = false;

  constructor(
    private menuService: MxMenuService,
    private identityService: MxIdentityService,
    private router: Router,
    public shell: SzShellService
  ) {}

  ngOnInit(): void {
    this.buildEntries();
    this.buildUser();
    this.onResize();
    this.subs.add(this.shell.collapsed$.subscribe((c) => (this.collapsed = c || this.forcedCollapsed)));
    this.subs.add(this.shell.drawerOpen$.subscribe((o) => (this.drawerOpen = o)));
    this.subs.add(
      this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
        this.buildEntries();
        this.shell.closeDrawer();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  @HostListener('window:resize')
  onResize() {
    const w = window.innerWidth;
    this.narrow = w < 1024;
    this.forcedCollapsed = w >= 1024 && w < 1280;
    this.collapsed = this.shell.collapsed || this.forcedCollapsed;
  }

  isActive(entry: SzMenuEntry): boolean {
    if (entry.routerLink) {
      return this.router.isActive(this.router.createUrlTree(entry.routerLink), {
        paths: entry.routerLink.join('/') === '/' ? 'exact' : 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    }
    return (entry.children || []).some((c) => this.isActive(c));
  }

  go(entry: SzMenuEntry) {
    const target = entry.routerLink || entry.children?.find((c) => c.routerLink)?.routerLink;
    if (target) {
      this.router.navigate(target);
    }
  }

  toggleGroup(entry: SzMenuEntry) {
    entry.open = !entry.open;
  }

  toggleCollapse() {
    if (this.forcedCollapsed) {
      return;
    }
    this.shell.toggleCollapse();
  }

  logout() {
    this.identityService.logout();
  }

  private buildEntries() {
    const previousOpen = new Set(this.entries.filter((e) => e.open).map((e) => e.key));
    this.entries = (this.menuService.menu || [])
      .filter((m) => m.visible !== false && !m.separator)
      .map((m) => {
        const children = (m.items || [])
          .filter((c: any) => c.visible !== false)
          .map((c: any) => ({ key: c.label, label: c.label, icon: szMenuIcon(c.label), routerLink: c.routerLink }));
        const entry: SzMenuEntry = {
          key: m.label,
          label: m.label,
          icon: szMenuIcon(m.label),
          routerLink: m.items ? undefined : m.routerLink,
          children: m.items ? children : undefined,
        };
        if (entry.children) {
          entry.open = previousOpen.has(entry.key) || entry.children.some((c) => this.isActive(c));
        }
        return entry;
      })
      .filter((e) => e.routerLink || (e.children && e.children.length > 0));
  }

  private buildUser() {
    const a = this.identityService.accountValue;
    if (!a) {
      return;
    }
    const last = (a.lastName || '').trim();
    const first = (a.firstName || '').trim();
    this.userName = `${last} ${first}`.trim() || a.email || '';
    this.initials = `${last.charAt(0)}${first.charAt(0)}`.toUpperCase() || (a.email || '?').charAt(0).toUpperCase();
    switch (a.role) {
      case Role.Admin:
      case Role.Administrator:
        this.userSub = 'Pharmaroad · admin';
        break;
      case Role.LogisticsUser:
        this.userSub = 'Pharmaroad · logisztika';
        break;
      case Role.Pharmacist:
        this.userSub = 'Helyettesítő gyógyszerész';
        break;
      default:
        this.userSub = a.subscription?.name || '';
    }
  }
}
